/**
 * Landing → app attribution: the `tabbio_attr` cookie.
 *
 * GA4 knows which campaign brought a visitor to www.tabbio.com. Nothing knows
 * which campaign brought a *paying user*, because the signup happens on
 * app.tabbio.com and the payment is a RevenueCat webhook with no browser in
 * sight. This module is the contract that carries the campaign across that gap:
 *
 *   1. the marketing site describes each landing (utm_*, an ad click id, or an
 *      external referrer) as a touch and keeps first and last touch in one
 *      first-party cookie scoped to `.tabbio.com`;
 *   2. the browser sends that cookie with the signup request to api.tabbio.com,
 *      where the API reads it once and writes a `UserAcquisition` row;
 *   3. revenue events join to that row, so "MRR by source" is a database query.
 *
 * Writer (apps/web) and reader (apps/api) both import this file, so the two
 * cannot disagree about the cookie's name, shape or limits: the partner referral
 * cookie lost attribution silently for exactly that reason once.
 *
 * Contents are campaign parameters only. No identifiers, no IP address, no user
 * agent. The landing page is a pathname with no query string, and the referrer is
 * an origin plus path with no query string, so a token in either can never end up
 * in the cookie. The cookie is written only after the visitor grants analytics
 * consent (tracker decision D4).
 *
 * Attribution rules follow GA4 so the two agree: first touch is set once and
 * never overwritten; last touch is "last non-direct", so a returning direct visit
 * does not erase the campaign that actually brought the person back.
 */

export const ATTRIBUTION_COOKIE_NAME = "tabbio_attr";

/** 90 days, the same window as the partner referral cookie and GA4's default. */
export const ATTRIBUTION_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

export const ATTRIBUTION_COOKIE_VERSION = 1 as const;

/**
 * The registrable domain the cookie is scoped to in production, so that
 * app.tabbio.com and api.tabbio.com receive what www.tabbio.com wrote. Any other
 * hostname (localhost, a preview deployment) gets a host-only cookie, which still
 * lets a local API on the same host read it in development.
 */
export const ATTRIBUTION_COOKIE_APEX_DOMAIN = "tabbio.com";

export const DIRECT_SOURCE = "(direct)";
export const DIRECT_MEDIUM = "(none)";
export const REFERRAL_MEDIUM = "referral";
export const UNSET_MEDIUM = "(not set)";

export const UTM_PARAMETERS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

/**
 * Ad click identifiers and the source / medium GA4 infers from each, for a paid
 * click whose link carries no utm_* (auto-tagging is the default in Google Ads
 * and Microsoft Advertising). Checked in order; the first present wins.
 */
export const CLICK_ID_SOURCES: readonly {
  parameter: string;
  source: string;
  medium: string;
}[] = [
  { parameter: "gclid", source: "google", medium: "cpc" },
  { parameter: "gbraid", source: "google", medium: "cpc" },
  { parameter: "wbraid", source: "google", medium: "cpc" },
  { parameter: "msclkid", source: "bing", medium: "cpc" },
  { parameter: "fbclid", source: "facebook", medium: "paid_social" },
  { parameter: "ttclid", source: "tiktok", medium: "paid_social" },
  { parameter: "li_fat_id", source: "linkedin", medium: "paid_social" },
  { parameter: "twclid", source: "twitter", medium: "paid_social" },
];

/** Longest value kept for any single field; longer input is cut, not rejected. */
export const ATTRIBUTION_MAX_FIELD_LENGTH = 200;

/** Cookies over ~4 KB are dropped by browsers; a full record is well under 1 KB. */
export const ATTRIBUTION_MAX_COOKIE_LENGTH = 3_000;

export type AttributionTouch = {
  /** Lowercased. `(direct)` when nothing identified the visit. */
  source: string;
  /** Lowercased. `(none)` for direct, `referral` for an untagged external link. */
  medium: string;
  campaign?: string;
  content?: string;
  term?: string;
  /** Pathname of the landing document, no query string or hash. */
  landingPage?: string;
  /** Origin plus pathname of an external referrer, no query string or hash. */
  referrer?: string;
  /** ISO 8601 timestamp of the landing. */
  at: string;
};

export type AttributionRecord = {
  v: typeof ATTRIBUTION_COOKIE_VERSION;
  first: AttributionTouch;
  last: AttributionTouch;
};

// Control characters cannot be meaningful in a campaign name and would let a
// crafted URL smuggle a header break into the cookie.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/g;

function clean(value: unknown, maxLength = ATTRIBUTION_MAX_FIELD_LENGTH) {
  if (typeof value !== "string") return undefined;
  const trimmed = value.replace(CONTROL_CHARACTERS, "").trim();
  if (!trimmed) return undefined;
  return trimmed.length > maxLength ? trimmed.slice(0, maxLength) : trimmed;
}

function lower(value: unknown) {
  const cleaned = clean(value);
  return cleaned ? cleaned.toLowerCase() : undefined;
}

function toUrl(value: string | URL): URL | null {
  if (value instanceof URL) return value;
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function hostWithoutWww(hostname: string) {
  return hostname.toLowerCase().replace(/^www\./, "");
}

/**
 * Whether two hostnames belong to the same site for attribution purposes. Any
 * two hosts under the production apex are one site (www → app is not a
 * referral); elsewhere only an identical host is.
 */
export function isSameSite(hostA: string, hostB: string) {
  const a = hostA.toLowerCase();
  const b = hostB.toLowerCase();
  if (a === b) return true;
  const apex = ATTRIBUTION_COOKIE_APEX_DOMAIN;
  const underApex = (host: string) =>
    host === apex || host.endsWith(`.${apex}`);
  return underApex(a) && underApex(b);
}

export function isDirectTouch(
  touch: Pick<AttributionTouch, "source" | "medium">,
) {
  return touch.source === DIRECT_SOURCE && touch.medium === DIRECT_MEDIUM;
}

/**
 * Describes one landing as a touch, or returns null when the document is not a
 * landing at all: an internal navigation that carried no campaign parameters.
 *
 * Precedence mirrors GA4: explicit utm_* first, then a known ad click id, then
 * an external referrer, then direct. Campaign parameters win even over an
 * internal referrer, because a tagged internal link is a deliberate act by
 * whoever built it.
 */
export function describeLanding(input: {
  url: string | URL;
  referrer?: string | null;
  now?: Date;
}): AttributionTouch | null {
  const url = toUrl(input.url);
  if (!url) return null;

  const at = (input.now ?? new Date()).toISOString();
  const landingPage = clean(url.pathname, 300);
  const params = url.searchParams;

  const utmSource = lower(params.get("utm_source"));
  if (utmSource) {
    return stripUndefined({
      source: utmSource,
      medium: lower(params.get("utm_medium")) ?? UNSET_MEDIUM,
      campaign: lower(params.get("utm_campaign")),
      content: lower(params.get("utm_content")),
      term: lower(params.get("utm_term")),
      landingPage,
      referrer: externalReferrer(input.referrer, url.hostname),
      at,
    });
  }

  const clickSource = CLICK_ID_SOURCES.find((entry) => {
    const value = params.get(entry.parameter);
    return typeof value === "string" && value.trim().length > 0;
  });
  if (clickSource) {
    return stripUndefined({
      source: clickSource.source,
      medium: clickSource.medium,
      campaign: lower(params.get("utm_campaign")),
      landingPage,
      referrer: externalReferrer(input.referrer, url.hostname),
      at,
    });
  }

  const referrerUrl = input.referrer ? toUrl(input.referrer) : null;
  if (referrerUrl) {
    if (isSameSite(referrerUrl.hostname, url.hostname)) {
      // Moving around our own pages: not a landing.
      return null;
    }
    return stripUndefined({
      source: hostWithoutWww(referrerUrl.hostname),
      medium: REFERRAL_MEDIUM,
      landingPage,
      referrer: externalReferrer(input.referrer, url.hostname),
      at,
    });
  }

  return stripUndefined({
    source: DIRECT_SOURCE,
    medium: DIRECT_MEDIUM,
    landingPage,
    at,
  });
}

function externalReferrer(
  referrer: string | null | undefined,
  landingHost: string,
) {
  if (!referrer) return undefined;
  const url = toUrl(referrer);
  if (!url || isSameSite(url.hostname, landingHost)) return undefined;
  // Origin and path only. A referrer's query string is the classic place for a
  // session token or an email address to leak from.
  return clean(`${url.origin}${url.pathname}`, 300);
}

function stripUndefined(touch: AttributionTouch): AttributionTouch {
  return Object.fromEntries(
    Object.entries(touch).filter(([, value]) => value !== undefined),
  ) as AttributionTouch;
}

/**
 * Folds a new landing into the stored record. First touch is immutable; last
 * touch follows GA4's "last non-direct click", so a direct return visit keeps
 * the campaign that preceded it.
 */
export function mergeAttribution(
  existing: AttributionRecord | null,
  touch: AttributionTouch,
): AttributionRecord {
  if (!existing) {
    return { v: ATTRIBUTION_COOKIE_VERSION, first: touch, last: touch };
  }
  return {
    v: ATTRIBUTION_COOKIE_VERSION,
    first: existing.first,
    last: isDirectTouch(touch) ? existing.last : touch,
  };
}

function parseTouch(value: unknown): AttributionTouch | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const source = lower(raw.source);
  const medium = lower(raw.medium);
  const at = clean(raw.at, 40);
  if (!source || !medium || !at || Number.isNaN(Date.parse(at))) return null;
  return stripUndefined({
    source,
    medium,
    campaign: lower(raw.campaign),
    content: lower(raw.content),
    term: lower(raw.term),
    landingPage: clean(raw.landingPage, 300),
    referrer: clean(raw.referrer, 300),
    at,
  });
}

/**
 * Parses a cookie value (percent-encoded or not) into a record, or null for
 * anything that is not a well-formed record of the current version. Never
 * throws: this runs on the signup path.
 */
export function parseAttributionRecord(raw: unknown): AttributionRecord | null {
  if (typeof raw !== "string" || !raw) return null;
  if (raw.length > ATTRIBUTION_MAX_COOKIE_LENGTH) return null;

  let text = raw;
  try {
    text = decodeURIComponent(raw);
  } catch {
    // Not percent-encoded, or malformed; try it as-is.
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
    return null;

  const record = parsed as Record<string, unknown>;
  if (record.v !== ATTRIBUTION_COOKIE_VERSION) return null;
  const first = parseTouch(record.first);
  const last = parseTouch(record.last);
  if (!first || !last) return null;
  return { v: ATTRIBUTION_COOKIE_VERSION, first, last };
}

export function serializeAttributionRecord(record: AttributionRecord) {
  return encodeURIComponent(JSON.stringify(record));
}

/**
 * `Domain` attribute for a cookie written from `hostname`, or null for a
 * host-only cookie. Only the production apex is ever widened, because a
 * `Domain` a browser refuses (a public suffix such as vercel.app) would drop the
 * cookie outright rather than fall back.
 */
export function attributionCookieDomain(hostname: string): string | null {
  const host = hostname.toLowerCase();
  const apex = ATTRIBUTION_COOKIE_APEX_DOMAIN;
  return host === apex || host.endsWith(`.${apex}`) ? `.${apex}` : null;
}

function cookieAttributes(hostname: string, secure: boolean, maxAge: number) {
  const attributes = ["Path=/"];
  const domain = attributionCookieDomain(hostname);
  if (domain) attributes.push(`Domain=${domain}`);
  attributes.push(`Max-Age=${maxAge}`);
  // Lax: sent on the top-level navigation from www to app and on the app's
  // same-site fetches to the API, never on a cross-site request.
  attributes.push("SameSite=Lax");
  // Secure on an http origin makes the browser drop the cookie, so it is
  // conditional for local development.
  if (secure) attributes.push("Secure");
  return attributes.join("; ");
}

/**
 * The string to assign to `document.cookie`. Callers pass the current hostname
 * and whether the page is https rather than reading globals, so this stays
 * testable under node.
 */
export function buildAttributionCookie(input: {
  record: AttributionRecord;
  hostname: string;
  secure: boolean;
  maxAgeSeconds?: number;
}) {
  const value = serializeAttributionRecord(input.record);
  return `${ATTRIBUTION_COOKIE_NAME}=${value}; ${cookieAttributes(
    input.hostname,
    input.secure,
    input.maxAgeSeconds ?? ATTRIBUTION_COOKIE_MAX_AGE_SECONDS,
  )}`;
}

/** Expires the cookie: assigned when the visitor withdraws analytics consent. */
export function buildAttributionCookieRemoval(input: {
  hostname: string;
  secure: boolean;
}) {
  return `${ATTRIBUTION_COOKIE_NAME}=; ${cookieAttributes(input.hostname, input.secure, 0)}`;
}

/**
 * Reads one cookie out of a raw `Cookie` header (or `document.cookie`). The
 * first readable match wins, matching the order browsers send them in. Values
 * are unquoted and percent-decoded; a malformed header yields null.
 */
export function readCookieValue(
  header: string | null | undefined,
  name: string,
): string | null {
  if (!header || typeof header !== "string") return null;

  for (const part of header.split(";")) {
    const separator = part.indexOf("=");
    if (separator === -1) continue;
    if (part.slice(0, separator).trim() !== name) continue;

    let raw = part.slice(separator + 1).trim();
    if (raw.length >= 2 && raw.startsWith('"') && raw.endsWith('"')) {
      raw = raw.slice(1, -1);
    }
    if (!raw) continue;
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }

  return null;
}

/** The GA4 client cookie: `_ga=GA1.1.<random>.<first visit timestamp>`. */
export const GA_CLIENT_COOKIE_NAME = "_ga";

/** The per-stream session cookie: `_ga_<measurement id without the G- prefix>`. */
export function gaStreamCookieName(measurementId: string) {
  return `_ga_${measurementId.trim().replace(/^G-/i, "")}`;
}

/**
 * Extracts the Measurement Protocol `client_id` from a `_ga` cookie value. The
 * id is the last two dot-separated numbers; the prefix encodes the cookie
 * format and the domain depth, which is why it cannot be taken literally.
 */
export function parseGaClientId(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  const parts = value.trim().split(".");
  if (parts.length < 4) return null;
  const clientId = parts.slice(-2).join(".");
  return /^\d+\.\d+$/.test(clientId) ? clientId : null;
}

/**
 * Extracts the GA4 `session_id` from a stream cookie value. Two formats exist:
 * `GS1.1.<session id>.<count>...` and, since 2025, `GS2.1.s<session id>$o<count>...`.
 */
export function parseGaSessionId(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  const trimmed = value.trim();

  const gs2 = /^GS2\.\d+\.s(\d+)/.exec(trimmed);
  if (gs2) return gs2[1] ?? null;

  const gs1 = /^GS1\.\d+\.(\d+)/.exec(trimmed);
  if (gs1) return gs1[1] ?? null;

  return null;
}

/** Campaign links use one convention before any vendor sees the URL. */
export function buildCampaignUrl(
  url: string,
  campaign: {
    source: string;
    medium: string;
    campaign: string;
    content?: string;
    term?: string;
  },
): string {
  const result = new URL(url);
  for (const [field, value] of Object.entries(campaign)) {
    const normalized = lower(value);
    if (normalized) result.searchParams.set(`utm_${field}`, normalized);
  }
  return result.toString();
}
